import { FC, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Alert, Snackbar, StrongModalText } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import { usePermissions, useURLparams } from '@hooks';
import { FullWidthContent, MainMenuLayout } from '@layouts';
import { Table } from '@molecules';
import { DEFAULT_ROWS_PER_PAGE, pendingProspectsColumns } from './constants';
import useGetPendingProspects, {
  PendingProspect,
  PendingProspectStatus,
} from './data/useGetPendingProspects';
import useResolvePendingProspect from './data/useResolvePendingProspect/hooks/useResolvePendingProspect';
import SearchBoxPendingProspects from './searchBox';
import { PendingProspectsSearchFormValues } from './searchBox/constants';

interface PendingProspectsParams {
  page: string;
  limit: string;
  status?: PendingProspectStatus | '';
  customerId?: string;
}

interface PendingProspectRow {
  prospect: PendingProspect;
  callback: ReturnType<typeof useResolvePendingProspect>['onActionClick'];
}

/**
 * Screen to approve or reject pending prospect registrations
 */
const PendingProspectsScreen: FC = () => {
  const navigate = useNavigate();
  const canUpdatePendingProspects = usePermissions([PERMISSIONS.updatePendingProspects]);
  const { params, setURLparams } = useURLparams<PendingProspectsParams>({
    page: '1',
    limit: String(DEFAULT_ROWS_PER_PAGE),
  });

  const {
    data: response,
    isLoading,
    handlePageChange,
    handleRowsPerPageChange,
    handleRefresh,
  } = useGetPendingProspects({
    params,
    setURLparams,
  });

  const {
    handleSubmit,
    onActionClick,
    isModalOpen,
    selectedProspect,
    setIsModalOpen,
    isError,
    setIsError,
    setIsSuccess,
    isSuccess,
    alertMessage,
    isLoading: isLoadingAction,
  } = useResolvePendingProspect(() => {
    handleRefresh();
  });

  const items = useMemo(() => {
    return (response?.page || []).map((item) => ({
      prospect: item,
      callback: onActionClick,
    }));
  }, [onActionClick, response?.page]);

  const columns = useMemo(
    () =>
      canUpdatePendingProspects
        ? pendingProspectsColumns
        : pendingProspectsColumns.filter((column) => column.title !== 'COMMON:ACTIONS'),
    [canUpdatePendingProspects]
  );

  const rowsPerPage = Number(params?.limit || DEFAULT_ROWS_PER_PAGE);
  const filters: PendingProspectsSearchFormValues = {
    status: params?.status || '',
    customerId: params?.customerId || '',
  };

  /**
   * Applies search filters and resets to first page
   * @param nextFilters - Selected search filters
   */
  const handleSearch = (nextFilters: PendingProspectsSearchFormValues) => {
    setURLparams({
      page: '1',
      limit: String(rowsPerPage),
      ...(nextFilters.status && { status: nextFilters.status }),
      ...(nextFilters.customerId && { customerId: nextFilters.customerId }),
    });
  };

  /**
   * Navigates to the pending prospect detail screen
   * @param row - Selected table row
   */
  const handleRowClick = (row: PendingProspectRow) => {
    navigate(`/prealta/gestion-pendientes/${row.prospect.id}`, { state: row.prospect });
  };

  return (
    <MainMenuLayout>
      <FullWidthContent>
        <SearchBoxPendingProspects filters={filters} onSearch={handleSearch} />
        <Table
          columns={columns}
          count={response?.totalCount || 0}
          isLoading={isLoading}
          items={items}
          page={params?.page || 1}
          rowsPerPage={params?.limit || DEFAULT_ROWS_PER_PAGE}
          onItemClick={handleRowClick}
          //TODO: Cast the typing to avoid build errors.
          onPageChange={handlePageChange as () => void}
          onRowsPerPageChange={handleRowsPerPageChange as () => void}
        />
        <Alert
          variant='warning'
          modalVisible={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          handleSubmit={handleSubmit}
          closeButtonText='COMMON:CANCEL'
          title={t('PROSPECTS:SEND_EMAIL:MODAL:TITLE')}
          submitLoading={isLoadingAction}
          content={
            <Trans
              i18nKey={`PENDING_PROSPECTS:CONFIRMATION_MODAL:${selectedProspect.action}`}
              components={{ s: <StrongModalText /> }}
              values={{ ...selectedProspect, count: selectedProspect.usersCount }}
            />
          }
        />
        <Snackbar
          open={isError}
          message={t(alertMessage?.description)}
          severity={'error'}
          handleClose={() => setIsError(false)}
        />
        <Snackbar
          open={isSuccess}
          message={t(alertMessage?.description)}
          severity='success'
          handleClose={() => setIsSuccess(false)}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default PendingProspectsScreen;
